import React, { Fragment, useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import { useAuth } from "../context/AuthContext";
import { ButtonModalFormSaveClose } from "./ButtonModalSelection";

export const ALL_ROLE = "ALL";

export const formatRole = (role) => {
  if (role === ALL_ROLE) return "All Roles";
  if (!role.includes("_")) return role;
  return role
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const CertCreation = ({ certList, cert, onComplete, close = null }) => {
  const { authAxios } = useAuth();
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState(null);
  const [nameValid, setNameValid] = useState(false);
  const [mandatoryFlag, setMandatoryFlag] = useState(true);
  const [type, setType] = useState("Legal");
  const [emailTemplate, setEmailTemplate] = useState("ELEARNING");
  const [renewal, setRenewal] = useState(null);
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);

  const defaultNameRejectReason = "Invalid Name";
  const defaultDupRejectReason = "Duplicate Certificate Name";
  const [nameRejectReason, setNameRejectReason] = useState(
    defaultNameRejectReason,
  );

  useEffect(() => {
    if (cert === null || cert == undefined) return;

    setName(cert?.name);
    setType(cert?.type);
    setMandatoryFlag(cert?.mandatory);
    setEmailTemplate(cert?.emailTemplate ?? "ELEARNING");

    if (
      cert?.defaultLifeTimeDays === null ||
      cert?.defaultLifeTimeDays === undefined
    ) {
      setRenewal(0);
    } else {
      const years = cert?.defaultLifeTimeDays / 365;
      setRenewal(years);
    }
  }, [cert]);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await authAxios().get(
          "/api/backoffice/certs/medical_roles",
        );
        setAvailableRoles(response.data);
      } catch (err) {
        console.log("error fetching medical roles:");
        console.log(err);
      }
    };
    fetchRoles();
  }, []);

  useEffect(() => {
    // Empty is a valid state (cert not tied to any role), so preload exactly what
    // the cert has; a brand-new cert starts with nothing selected.
    setSelectedRoles(Array.isArray(cert?.medicalRoles) ? cert.medicalRoles : []);
  }, [cert]);

  const isAllRoles = selectedRoles.includes(ALL_ROLE);

  const toggleAllRoles = () => {
    setSelectedRoles((prev) => (prev.includes(ALL_ROLE) ? [] : [ALL_ROLE]));
  };

  const toggleRole = (role) => {
    setSelectedRoles((prev) => {
      let next = prev.filter((r) => r !== ALL_ROLE);
      if (next.includes(role)) {
        next = next.filter((r) => r !== role);
      } else {
        next = [...next, role];
      }
      return next;
    });
  };

  const checkDuplicateName = (certName) => {
    for (var i in certList) {
      const cert = certList[i];
      if (cert?.name === certName) return true;
    }
    return false;
  };

  useEffect(() => {
    const isEdit = cert?.id !== null && cert?.id !== undefined;
    const validText = name !== null && name !== undefined && name !== "";
    const duplicate = checkDuplicateName(name) && !isEdit;
    const valid = validText && !duplicate;

    setNameValid(valid);
    setNameRejectReason(
      duplicate ? defaultDupRejectReason : defaultNameRejectReason,
    );
  }, [name]);

  useEffect(() => {
    const valid = nameValid;
    setSaveDisabled(!valid);
  }, [nameValid]);

  const onSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const renewalParam = renewal > 0 ? renewal * 365 : null;
      const payload = {
        name: name.trim(),
        type: type,
        defaultLifeTimeDays: renewalParam,
        mandatory: mandatoryFlag,
        emailTemplate: emailTemplate,
        medicalRoles: selectedRoles,
      };

      var params = {};
      const isUpdate = cert?.id !== null && cert?.id !== undefined;
      if (isUpdate) params["cert_id"] = cert?.id;

      const config = {
        headers: {
          "content-type": "application/json",
        },
        params: params,
      };

      const response = await authAxios().post(
        "/api/backoffice/certs/" + (isUpdate ? "edit_cert" : "create_cert"),
        payload,
        config,
      );
      setLoading(false);

      if (onComplete !== null && onComplete !== undefined)
        onComplete(response.data);
    } catch (err) {
      setLoading(false);
      console.log("error:");
      console.log(err);
    }
  };

  const nameChanged = (value) => {
    var text = value.target.value;
    setName(text);
  };

  const typeChanged = (value) => {
    var text = value.target.value;
    setType(text);
  };

  const emailTemplateChanged = (value) => {
    setEmailTemplate(value.target.value);
  };

  const renewalChanged = (value) => {
    const text = value.target.value;
    setRenewal(text);
  };

  return (
    <Fragment>
      <Container fluid>
        <Form onSubmit={onSave}>
          <Form.Group className="mb-3" controlId="formName">
            <Row>
              <Col xs lg="2">
                <Form.Label>Name</Form.Label>
              </Col>
              <Col xs lg="4">
                <Form.Control
                  type="text"
                  value={name}
                  onChange={nameChanged}
                  isValid={nameValid}
                  isInvalid={!nameValid}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  {nameRejectReason}
                </Form.Control.Feedback>
              </Col>
            </Row>
          </Form.Group>

          <Form.Group className="mb-3" controlId="formType">
            <Row>
              <Col xs lg="2">
                <Form.Label>Type</Form.Label>
              </Col>
              <Col xs lg="4">
                <Form.Select value={type} onChange={typeChanged}>
                  <option value="Legal">Legal</option>
                  <option value="Medical">Medical</option>
                  <option value="Other">Other</option>
                </Form.Select>
              </Col>
            </Row>
          </Form.Group>

          <Form.Group className="mb-3" controlId="formEmailNotificationType">
            <Row>
              <Col xs lg="2">
                <Form.Label>Email Notification Type</Form.Label>
              </Col>
              <Col xs lg="4">
                <Form.Select
                  value={emailTemplate}
                  onChange={emailTemplateChanged}
                >
                  <option value="ELEARNING">E-learning</option>
                  <option value="FACE_TO_FACE">Face to face</option>
                </Form.Select>
              </Col>
            </Row>
          </Form.Group>

          <Form.Group className="mb-3" controlId="formPeriod">
            <Row>
              <Col xs lg="2">
                <Form.Label>Renewal Period</Form.Label>
              </Col>
              <Col xs lg="4">
                <Form.Select value={renewal} onChange={renewalChanged}>
                  <option value="0">Never</option>
                  <option value="1">1 Year</option>
                  <option value="2">2 Years</option>
                  <option value="3">3 Years</option>
                </Form.Select>
              </Col>
            </Row>
          </Form.Group>

          <Form.Group className="mb-3" controlId="formMandatory">
            <Row>
              <Col xs lg="2">
                <Form.Label>Mandatory</Form.Label>
              </Col>
              <Col xs lg="4">
                <Form.Check
                  className={"fs-3"}
                  type="checkbox"
                  checked={mandatoryFlag}
                  onChange={() => setMandatoryFlag(!mandatoryFlag)}
                />
              </Col>
            </Row>
          </Form.Group>

          <Form.Group className="mb-3" controlId="formRoles">
            <Row>
              <Col xs lg="2">
                <Form.Label>Roles</Form.Label>
              </Col>
              <Col xs lg="10">
                <Form.Check
                  type="checkbox"
                  id="role-all"
                  label={formatRole(ALL_ROLE)}
                  checked={isAllRoles}
                  onChange={toggleAllRoles}
                />
                <Row className="mt-2">
                  {availableRoles
                    .filter((role) => role !== ALL_ROLE)
                    .map((role) => (
                      <Col xs="6" lg="4" key={role}>
                        <Form.Check
                          type="checkbox"
                          id={`role-${role}`}
                          label={formatRole(role)}
                          checked={selectedRoles.includes(role)}
                          disabled={isAllRoles}
                          onChange={() => toggleRole(role)}
                        />
                      </Col>
                    ))}
                </Row>
              </Col>
            </Row>
          </Form.Group>
        </Form>
      </Container>

      <ButtonModalFormSaveClose
        saveName={"Save"}
        close={close}
        formSave={onSave}
        loading={loading || saveDisabled}
      />
    </Fragment>
  );
};
